import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { DatePicker, ConfigProvider } from 'antd';
import locale from 'antd/es/date-picker/locale/pt_BR';
import { Grid, createStyles, makeStyles, useTheme, IconButton, Tooltip } from '@material-ui/core';
import { SECONDARY_COLOR, FONT_FAMILY_PRIMARY, PRIMARY_COLOR, LIGHT_GRAY_COLOR, TEXT_SECONDARY_COLOR, RED, WHITE_BACKGROUND, PRIMARY_COLOR_LIGHT, SHADOW_GRAY_COLOR } from '../../theme';
import { X } from 'react-feather';

const DateRangePickerAntd = ({
  periodoMaximoEmDias,
  bloqueiaDataFutura,
  periodoHandler,
  dataInicial,
  dataFinal,
  error,
  setError,
  label,
  campoObrigatorio,
  className
}) => {
  const [dates, setDates] = useState(null);
  const [value, setValue] = useState([dataInicial ?? null, dataFinal ?? null] || null);
  const [mensagemErro, setMensagemErro] = useState('');
  const [semLimiteCalendario, setSemLimiteCalendario] = useState(false);
  const [focado, setFocado] = useState(false);

  const classes = useStyles();

  const theme = useTheme();

  useEffect(() => {
    if (error) {
      periodoHandler(null, null);

      if (!mensagemErro && campoObrigatorio) {
        setMensagemErro('Campo obrigatório');
      }
    }
  }, [error]);

  const disabledDate = (current) => {
    const removeAtributoTitulo = () => {
      const pickerCells = document.querySelectorAll('.ant-picker-cell');
      pickerCells.forEach((cell) => {
        cell.removeAttribute('title');
      });
    };

    removeAtributoTitulo();

    if (!dates) {
      return false;
    }

    if (semLimiteCalendario) {
      return false;
    }

    const possuiDataSelecionada = dates[0] || dates[1];
    const possuiPeriodoMaximoEmDias = periodoMaximoEmDias && periodoMaximoEmDias > 0;

    if (possuiDataSelecionada && possuiPeriodoMaximoEmDias) {
      const tooLate = dates[0] && current?.diff(dates[0], 'days') >= periodoMaximoEmDias;
      const tooEarly = dates[1] && dates[1]?.diff(current, 'days') >= periodoMaximoEmDias;

      if (bloqueiaDataFutura) {
        return !!tooEarly || !!tooLate || current > dayjs();
      }

      return !!tooEarly || !!tooLate;
    }

    if (bloqueiaDataFutura) {
      return current > dayjs();
    }

    return false;
  };

  const onOpenChange = (open) => {
    if (open) {
      setDates([null, null]);
    } else {
      setDates(null);

      if (value) {
        if ((!value[0] || !value[1]) && campoObrigatorio) {
          setError && setError(true);
        }
      }
    }
  };

  const onChange = (val) => {
    const dataInicio = val ? val[0] : null;
    const dataFim = val ? val[1] : null;

    let tooLate = false;
    if (periodoMaximoEmDias) {
      tooLate = val && val[0] && val[1] && val[1]?.diff(val[0], 'days') >= periodoMaximoEmDias;
    }

    if (tooLate) {
      setMensagemErro('Período máximo permitido: 12 meses');
      setError && setError(true);
    } else if (!val && campoObrigatorio) {
      setMensagemErro('Campo obrigatório');
      setError && setError(true);
    } else if (val && ((val[0] && val[0].isAfter(dayjs())) || (val[1] && val[1].isAfter(dayjs())))) {
      setMensagemErro('Não é permitido data futura');
      setError && setError(true);
      return;
    } else {
      setMensagemErro('');
      setError && setError(false);
    }

    if ((!dataInicio || !dataFim) && campoObrigatorio) {
      setError && setError(true);
    }

    periodoHandler(dataInicio?.startOf('day'), dataFim?.endOf("day"));
    setValue(val);
    setSemLimiteCalendario(false);
  };

  const onCalendarChange = (val) => {
    setDates(val);
  };

  const onChangeBlur = (event) => {
    setFocado(false);

    if (event.target.value) {
      const dataSelecionada = dayjs(event.target.value, 'DD/MM/YYYY');
      const placeholder = event.target.placeholder;

      if (dataSelecionada.isAfter(dayjs())) {
        setMensagemErro('Não é permitido data futura');
        setError && setError(true);
        return;
      }

      if (placeholder === 'Início') {
        setSemLimiteCalendario(false);
      }

      setDates((prevDates) => {
        if (prevDates) {
          const [, endDate] = prevDates;
          return [dataSelecionada, endDate];
        }
        return null;
      });
    }
  };

  const handleKeyDown = (e) => {
    const { key, currentTarget } = e;
    let valorData = currentTarget.value;

    if (
      (!/^\d$/.test(key) && key !== 'Backspace' && key !== 'Tab') ||
      (valorData.length === 10 && key !== 'Backspace' && key !== 'Tab')
    ) {
      e.preventDefault();
      return;
    }

    if (valorData.length === 7 && valorData.indexOf('/', 3) === -1) {
      valorData = `${valorData.slice(0, 2)}/${valorData.slice(2, 4)}/${valorData.slice(4)}`;
    }

    setSemLimiteCalendario(true);
    currentTarget.value = valorData;
  };

  const customClearIcon = (
    <Tooltip title="Limpar período">
      <IconButton
        className={classes.iconButton}
        aria-label='Clear'
      >
        <X size={20} id="limpar-periodo" aria-label="Limpar período" className={classes.customCloseIcon} />
      </IconButton>
    </Tooltip>
  );

  const definirCorLabel = () => {
    if (error) {
      return RED;
    } else if (focado) {
      return SECONDARY_COLOR;
    }
    return '';
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          fontFamily: FONT_FAMILY_PRIMARY,
          borderRadius: 4,
          controlHeight: 35,
          fontSize: 16,
          sizePopupArrow: 5,
          colorError: theme.palette.error.main,
          colorPrimary: PRIMARY_COLOR_LIGHT,
          colorPrimaryHover: PRIMARY_COLOR,
          colorBorder: LIGHT_GRAY_COLOR,
          colorTextPlaceholder: error ? theme.palette.error.main : TEXT_SECONDARY_COLOR,
          colorTextBase: PRIMARY_COLOR,
          colorPrimaryBg: LIGHT_GRAY_COLOR,
        },
      }}
    >
      <label className={classes.label} style={{ color: definirCorLabel() }}>
        {label}
      </label>
      <DatePicker.RangePicker
        locale={locale}
        className={`${classes.rangePicker} ${className}`}
        format="DD/MM/YYYY"
        allowClear={{ clearIcon: customClearIcon }}
        separator={
          <span
            style={{
              color: error ? theme.palette.error.main : TEXT_SECONDARY_COLOR,
              paddingRight: '10px',
            }}
          >
            -
          </span>
        }
        value={value}
        disabledDate={disabledDate}
        onCalendarChange={onCalendarChange}
        onChange={onChange}
        onOpenChange={onOpenChange}
        changeOnBlur
        onFocus={() => setFocado(true)}
        onKeyDown={handleKeyDown}
        placeholder={['Início', 'Fim']}
        placement="bottomRight"
        status={error ? 'error' : ''}
        showTime={false}
        onBlur={onChangeBlur}
        getPopupContainer={(triggerNode) => {
          return triggerNode.parentNode;
        }}
      />
      {error && (
        <Grid item container justifyContent="flex-start" className={classes.errorMessage}>
          {mensagemErro}
        </Grid>
      )}
    </ConfigProvider>
  );
};
const useStyles = makeStyles((theme) =>
  createStyles({
    rangePicker: {
      width: '100%',
      height: '56px',
      cursor: 'pointer',
      '& .ant-picker-suffix': {
        color: TEXT_SECONDARY_COLOR,
        padding: '12px',
      },
      '& .ant-picker-input >input': {
        color: PRIMARY_COLOR,
      },
      '&.ant-picker-focused': {
        borderColor: SECONDARY_COLOR,
        boxShadow: '0 0 0 1px rgba(191, 67, 171)',
      },
      '&:hover .ant-picker-suffix': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
        borderRadius: '50%'
      },
    },
    customCloseIcon: {
      color: TEXT_SECONDARY_COLOR,
    },
    errorMessage: {
      position: 'absolute',
      width: '300px',
      color: theme.palette.error.main,
      fontSize: '12px',
      padding: '5px 15px',
    },
    label: {
      position: 'absolute',
      width: 'fit-content',
      padding: '0 5px',
      margin: '-8px 0 0 10px',
      textAlign: 'center',
      zIndex: 2,
      backgroundColor: WHITE_BACKGROUND,
      fontSize: '12px',
      fontFamily: 'CircularStd-Book',
      color: SHADOW_GRAY_COLOR
    },
    iconButton: {
      padding: '10px',
    }
  }),
);

export default DateRangePickerAntd;
